import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Tent, MapPin, Flag } from 'lucide-react';
import { CONDITION_TAGS, conditionAge, deleteConditionReport } from '../../services/conditionService';
import { calculateOccupancyUntil, occupancyEndDate } from '../../services/occupancyService';
import { getDateLocale } from '../../services/localeHelpers';
import type { CampingSpot, AppUser, SpotCondition, ConditionTag } from '../../types';

const DURATION_OPTIONS = [1, 2, 3, 5, 7, 14];

interface SpotVisitModalProps {
  spot: CampingSpot & { distance?: number };
  user: AppUser;
  conditions: SpotCondition[];
  onClose: () => void;
  onConfirm: (params: { days: number; tags: ConditionTag[] }) => void;
}

const SpotVisitModal: React.FC<SpotVisitModalProps> = ({ spot, user, conditions, onClose, onConfirm }) => {
  const { t, i18n } = useTranslation();
  const [selectedDays, setSelectedDays] = useState(1);
  const [selectedTags, setSelectedTags] = useState<ConditionTag[]>([]);

  const toggleTag = (tag: ConditionTag) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const endDate = occupancyEndDate(
    calculateOccupancyUntil(selectedDays),
    getDateLocale()
  );

  // Portal til document.body — åpnes fra SpotCard inne i SidebarPanel, som med
  // `.glass-strong` (backdrop-filter) + z-mapOverlay låser `fixed`-barn til sitt
  // eget 2000-lag. Uten portal maler MobileBottomNav (3000) over bekreft-knappen.
  return createPortal(
    <div
      className="fixed inset-0 z-[10004] flex items-end justify-center sm:items-center animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative bg-[var(--bg-surface)] rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md shadow-2xl animate-in slide-in-from-bottom-4 duration-300 max-h-[90dvh] overflow-y-auto"
        style={{ overscrollBehavior: 'contain', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Handle bar (mobile) */}
        <div className="sm:hidden flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-[var(--border-secondary)]" />
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <X size={18} />
        </button>

        <div className="px-6 pt-5 pb-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-2xl bg-emerald-100 flex items-center justify-center">
              <Tent size={22} className="text-emerald-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-black text-[var(--text-primary)] leading-tight truncate">
                {t('spot.occupied.modal_title')}
              </h2>
              <p className="text-tiny text-[var(--text-muted)] mt-0.5 flex items-center gap-1 truncate">
                <MapPin size={10} className="shrink-0" />
                <span className="truncate">{spot.name}</span>
                {spot.distance != null && (
                  <span className="shrink-0 ml-1">
                    ({spot.distance < 1000 ? `${Math.round(spot.distance)}m` : `${(spot.distance / 1000).toFixed(1)}km`})
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Day selector */}
          <div className="mb-5">
            <p className="text-tiny font-black uppercase tracking-widest text-[var(--text-muted)] mb-2.5">
              {t('spot.occupied.how_long')}
            </p>
            <div className="flex flex-wrap gap-2">
              {DURATION_OPTIONS.map(d => (
                <button
                  key={d}
                  onClick={() => setSelectedDays(d)}
                  className={`px-4 py-2 rounded-xl text-sm font-bold transition-all active:scale-95 ${
                    selectedDays === d
                      ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25'
                      : 'bg-[var(--bg-input)] text-[var(--text-secondary)] border border-[var(--border-primary)] hover:border-emerald-400'
                  }`}
                >
                  {d} {d === 1 ? t('spot.occupied.day') : t('spot.occupied.days')}
                </button>
              ))}
            </div>
          </div>

          {/* Active condition reports */}
          {conditions.length > 0 && (
            <div className="mb-4">
              <div className="flex flex-wrap gap-1.5">
                {conditions.map(c => {
                  const meta = CONDITION_TAGS.find(ct => ct.id === c.tag);
                  if (!meta) return null;
                  const isOwn = c.userId === user.uid;
                  return (
                    <div
                      key={c.id}
                      className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs font-bold ${meta.color} ${meta.textColor}`}
                    >
                      <span>{meta.emoji}</span>
                      <span>{t(meta.labelKey)}</span>
                      <span className="opacity-60">· {conditionAge(c.createdAt)}</span>
                      {isOwn && (
                        <button
                          onClick={e => { e.stopPropagation(); deleteConditionReport(c.id); }}
                          className="ml-0.5 opacity-60 hover:opacity-100"
                          title={t('spot.conditions.remove_report')}
                        >×</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Condition tags */}
          <div className="mb-5">
            <p className="text-tiny font-black uppercase tracking-widest text-[var(--text-muted)] mb-2.5">
              {t('spot.occupied.condition_now')}{' '}
              <span className="font-medium normal-case tracking-normal">{t('spot.occupied.condition_optional')}</span>
            </p>
            <div className="flex flex-wrap gap-1.5">
              {CONDITION_TAGS.map(meta => {
                const alreadyReported = conditions.some(c => c.tag === meta.id && c.userId === user.uid);
                const isSelected = selectedTags.includes(meta.id);
                return (
                  <button
                    key={meta.id}
                    disabled={alreadyReported}
                    onClick={() => toggleTag(meta.id)}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-2xs font-bold transition-all active:scale-95 ${
                      alreadyReported
                        ? `${meta.color} ${meta.textColor} opacity-40 cursor-default`
                        : isSelected
                          ? `${meta.color} ${meta.textColor} ring-2 ring-offset-1 ring-current`
                          : `${meta.color} ${meta.textColor} hover:opacity-80`
                    }`}
                  >
                    {meta.emoji} {t(meta.labelKey)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Info text */}
          <p className="text-tiny text-[var(--text-muted)] mb-4 leading-relaxed">
            {t('spot.occupied.info', { days: selectedDays, date: endDate })}
          </p>

          {/* Confirm button */}
          <button
            onClick={() => onConfirm({ days: selectedDays, tags: selectedTags })}
            className="w-full py-3 rounded-2xl bg-emerald-500 text-white font-black text-sm uppercase tracking-wider hover:bg-emerald-600 active:scale-[0.98] transition-all shadow-lg shadow-emerald-500/25"
          >
            {t('spot.occupied.confirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SpotVisitModal;
